import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { championshipsApi } from './api';

const POLL_INTERVAL = 30000; // 30 seconds fallback

export function useLiveStandings(championshipId) {
  const [standings, setStandings] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [topAssists, setTopAssists] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const socketRef = useRef(null);

  const fetchStandings = useCallback(async () => {
    if (!championshipId) return;
    try {
      const [sRes, scRes, asRes] = await Promise.all([
        championshipsApi.standings(championshipId),
        championshipsApi.topScorers(championshipId, 10),
        championshipsApi.topAssists(championshipId, 10),
      ]);
      setStandings(sRes.data || []);
      setTopScorers(scRes.data || []);
      setTopAssists(asRes.data || []);
      setLastUpdate(new Date());
    } catch (e) {
      console.error('Error fetching standings:', e);
    }
  }, [championshipId]);

  useEffect(() => {
    if (!championshipId) return;
    fetchStandings();

    // WebSocket connection
    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, { transports: ['websocket', 'polling'], reconnectionAttempts: 5 });
    socketRef.current = socket;

    socket.on('standings_updated', (data) => {
      if (!data?.championship_id || data.championship_id === championshipId) {
        fetchStandings();
      }
    });

    // Polling fallback
    const interval = setInterval(fetchStandings, POLL_INTERVAL);

    return () => {
      clearInterval(interval);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [championshipId, fetchStandings]);

  return { standings, topScorers, topAssists, lastUpdate, refresh: fetchStandings };
}
